import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import * as THREE from 'three';
import './AsynscaleHome.css';

const AsynscaleHome = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true });
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 1, 1000);
    camera.position.z = 120;
    renderer.setSize(window.innerWidth, window.innerHeight);

    const geo = new THREE.BufferGeometry();
    const pts = [];
    for (let i = 0; i < 2000; i++) {
      pts.push((Math.random()-0.5)*400,(Math.random()-0.5)*400,(Math.random()-0.5)*400);
    }
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pts,3));
    const mat = new THREE.PointsMaterial({ color: 0x4f8cff, size: 0.8, opacity: 0.6, transparent: true });
    const cloud = new THREE.Points(geo, mat);
    scene.add(cloud);

    let animationId;
    function animate() {
      animationId = requestAnimationFrame(animate);
      cloud.rotation.y += 0.0006;
      renderer.render(scene, camera);
    }
    animate();

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', handleResize);
      renderer.dispose();
    };
  }, []);

  return (
    <div className="asynscale-home">
      <header>
        <div className="nav">
          <div className="logo">Asyn<span>scale</span>AI</div>
          <nav>
            <a href="#system">System</a>
            <a href="#engines">Engines</a>
            <a href="#domains">Domains</a>
            <a href="#about">About</a>
            <Link to="/leaderboard">Leaderboard</Link>
          </nav>
        </div>
      </header>

      <section id="hero">
        <canvas ref={canvasRef} id="bg-canvas"></canvas>
        <div className="hero-content">
          <span>System-first Autonomous Intelligence</span>
          <h1>
            Autonomy is not a product.<br/>
            It is a coherent system.
          </h1>
          <p>
            自动智能不是算法拼装，而是一个在感知、建模、决策与执行之间<br/>
            可闭合、可演化、可验证的系统整体。
          </p>
        </div>
      </section>

      <section id="system">
        <div className="section-intro">
          <h2>Autonomy as a System</h2>
          <p>
            We design autonomous intelligence from first principles — not as isolated models,
            but as a unified system that reasons about the world, acts under constraints,
            and improves through interaction.
            <br/><br/>
            我们以系统工程为起点构建自动智能，而非孤立模型。
          </p>
        </div>

        <div className="metrics">
          <div className="metric">
            <strong>&lt; 50ms</strong>
            Closed-loop latency<br/>闭环延迟
          </div>
          <div className="metric">
            <strong>99.99%</strong>
            Safety constraint satisfaction<br/>安全约束满足率
          </div>
          <div className="metric">
            <strong>Cross-domain</strong>
            Generalization<br/>跨场景泛化
          </div>
        </div>
      </section>

      <div className="divider"></div>

      <section id="engines">
        <div className="section-intro">
          <h2>System Engines</h2>
          <p>
            Each engine is independently verifiable, yet designed to function
            as part of a tightly coupled autonomous stack.
            <br/><br/>
            每一个模块可独立验证，但只在系统中发挥完整价值。
          </p>
        </div>

        <div className="grid grid-3">
          <div className="card">
            <h3>Perception Engine</h3>
            <p>
              Cross-modal geometric encoding with uncertainty awareness,
              enabling stable spatial inference under incomplete observations.
              <br/><br/>
              跨模态几何感知与不确定性建模。
            </p>
          </div>

          <div className="card">
            <h3>World Model</h3>
            <p>
              Neural state transition models supporting counterfactual rollout
              and long-horizon prediction.
              <br/><br/>
              神经世界模型与反事实推演。
            </p>
          </div>

          <div className="card">
            <h3>Decision Stack</h3>
            <p>
              Hierarchical policies constrained by formal safety envelopes,
              optimized for long-term objectives.
              <br/><br/>
              分层决策与形式化安全约束。
            </p>
          </div>

          <div className="card">
            <h3>Embodied Interface</h3>
            <p>
              Sensor–actuator co-design with latency-bounded control loops,
              closing the perception–action gap.
              <br/><br/>
              具身接口与实时控制闭环。
            </p>
          </div>
        </div>
      </section>

      <div className="divider"></div>

      <section id="domains">
        <div className="section-intro">
          <h2>Deployment Domains</h2>
          <p>
            Our systems are designed to scale across environments where uncertainty,
            safety, and long-horizon reasoning are first-order constraints.
          </p>
        </div>

        <div className="grid grid-3">
          <div className="card">
            <h3>Urban Autonomy</h3>
            <p>
              Dense, dynamic environments with high safety requirements.
              <br/>城市级自动驾驶。
            </p>
          </div>
          <div className="card">
            <h3>Industrial Sites</h3>
            <p>
              Structured yet safety-critical operational domains.
              <br/>工业与园区自动化。
            </p>
          </div>
          <div className="card">
            <h3>Extreme Environments</h3>
            <p>
              Low-visibility, high-uncertainty conditions.
              <br/>极端与复杂环境。
            </p>
          </div>
        </div>
      </section>

      <section id="about">
        <h2>About AsynscaleAI</h2>
        <p>
          AsynscaleAI（徐图致远）致力于构建系统级自动智能。
        </p>
      </section>

      <footer>
        <p>© 2026 AsynscaleAI · 徐图致远</p>
      </footer>
    </div>
  );
};

export default AsynscaleHome;
